<template>
  <!-- Not dismissible: shown for as long as the charge is failing -->
  <Alert v-if="issue" variant="error" :square="square">
    <template v-if="issue.pastDeadline">
      {{ t("licensing.billing.paymentIssuePastDeadline") }}
    </template>
    <i18n-t v-else keypath="licensing.billing.paymentIssue" scope="global">
      <template #date>
        <strong :title="suspendsOnUtc">{{ suspendsOnLocal }}</strong>
      </template>
      <template #daysLeft>
        {{ t("licensing.billing.daysLeft", issue.daysUntilSuspension) }}
      </template>
    </i18n-t>
    <div v-if="errorKey" class="mt-1 text-xs font-medium">
      {{ t(errorKey) }}
    </div>

    <template #actions>
      <Button
        variant="primary"
        size="sm"
        type="button"
        :disabled="opening"
        :aria-busy="opening"
        @click="open"
      >
        <MdiLoading v-if="opening" class="animate-spin" aria-hidden="true" />
        {{ t("licensing.billing.updatePaymentMethod") }}
      </Button>
    </template>
  </Alert>
</template>

<script setup lang="ts">
import { computed, toRef } from "vue";
import { useI18n } from "vue-i18n";
import { getPaymentIssue, type Org } from "tf-api-client";
import { useBillingPortal } from "../../composables/use-billing-portal";
import i18nUtil from "../../utils/i18n";
import Alert from "../Base/Alert.vue";
import Button from "../Base/Button.vue";
import MdiLoading from "~icons/mdi/loading";

// Red countdown banner while a recurring charge of the organization's own
// subscription is failing. The parent decides whether it applies: never for a
// Managed organization, whose subscription is its parent's.
const props = defineProps<{
  orgId: string | null;
  subscription: Org.Subscription | null;
  // square corners: the app shell's full-width strip
  square?: boolean;
}>();

const { t } = useI18n();

// Against the browser clock, like the lifecycle banners.
const issue = computed(() =>
  props.subscription ? getPaymentIssue(props.subscription) : null,
);

const suspendsOnLocal = computed(() =>
  issue.value ? i18nUtil.formatLongDate(issue.value.suspendsOn) : "",
);
// UTC on hover
const suspendsOnUtc = computed(() =>
  issue.value ? issue.value.suspendsOn.toISOString() : "",
);

const { open, opening, errorKey } = useBillingPortal(toRef(props, "orgId"));
</script>
